import React, { useCallback, useMemo, useState } from "react"

const FACETS = [
  { id: "a", x: "6%", y: "22%", d: "0s" },
  { id: "b", x: "90%", y: "28%", d: "0.4s" },
  { id: "c", x: "8%", y: "68%", d: "0.9s" },
  { id: "d", x: "88%", y: "72%", d: "1.3s" },
  { id: "e", x: "18%", y: "42%", d: "0.6s", hide: "md" },
  { id: "f", x: "78%", y: "48%", d: "1.1s", hide: "md" },
]

/**
 * Profile wallpaper — identity aurora field.
 * Geometry only (no text/icons). Interactive edge facets respond to tap/hover.
 * `editing` brightens the field while form fields are focused.
 */
export default function ProfileScene({ layerRef, editing = false }) {
  const [lit, setLit] = useState(() => new Set())

  const ribbons = useMemo(
    () =>
      Array.from({ length: 4 }, (_, i) => ({
        i,
        top: `${20 + i * 16}%`,
        delay: `${i * 0.8}s`,
        dur: `${10 + i * 1.4}s`,
      })),
    []
  )

  const toggleFacet = useCallback((id) => {
    setLit((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const litCount = lit.size

  return (
    <div
      ref={layerRef}
      className={`profile-wallpaper fixed inset-0 z-[1] overflow-hidden${
        editing ? " profile-wallpaper--editing" : ""
      }`}
      style={{ "--profile-lit": String(litCount) }}
      aria-hidden
    >
      <div className="profile-aurora profile-aurora--a pointer-events-none" />
      <div className="profile-aurora profile-aurora--b pointer-events-none" />

      <div className="profile-ribbon-field pointer-events-none">
        {ribbons.map((r) => (
          <span
            key={r.i}
            className={`profile-ribbon ${r.i > 2 ? "hidden sm:block" : ""}`}
            style={{
              top: r.top,
              animationDelay: r.delay,
              animationDuration: r.dur,
            }}
          />
        ))}
      </div>

      {/* Identity core — ambient only */}
      <div className="profile-identity-core pointer-events-none">
        <span className="profile-identity-ring profile-identity-ring--outer" />
        <span className="profile-identity-ring profile-identity-ring--mid hidden sm:block" />
        <span className="profile-identity-ring profile-identity-ring--inner" />
        <span className="profile-identity-prism" />
        <span className="profile-identity-pulse" />
      </div>

      {/* Interactive facets — tap to light up (edge-placed) */}
      {FACETS.map((f) => (
        <span
          key={f.id}
          role="presentation"
          className={`profile-facet ${lit.has(f.id) ? "profile-facet--lit" : ""} ${
            f.hide === "md" ? "hidden md:block" : ""
          }`}
          style={{ left: f.x, top: f.y, animationDelay: f.d }}
          onClick={(e) => {
            e.stopPropagation()
            toggleFacet(f.id)
          }}
        />
      ))}

      <span className="profile-edge profile-edge--tl pointer-events-none" />
      <span className="profile-edge profile-edge--tr pointer-events-none" />
      <span className="profile-edge profile-edge--bl pointer-events-none hidden sm:block" />
      <span className="profile-edge profile-edge--br pointer-events-none hidden sm:block" />
    </div>
  )
}
